"use client";

import { useMemo, useState } from "react";

// Destination zones, keyed off a central US shipping hub. Multipliers and
// transit windows are indicative LTL-freight estimates, not a live rate quote.
const REGIONS = [
  { label: "West (CA, NV, AZ)", mult: 1.0, days: "2-3" },
  { label: "Pacific NW (OR, WA)", mult: 1.15, days: "3-4" },
  { label: "Mountain (CO, UT, ID)", mult: 1.2, days: "3-4" },
  { label: "Midwest (IL, OH, MI)", mult: 1.3, days: "4-5" },
  { label: "South (TX, FL, GA)", mult: 1.35, days: "4-6" },
  { label: "Northeast (NY, MA, PA)", mult: 1.5, days: "5-6" },
];

const UNITS_PER_PALLET = 150; // typical mixed-lot pallet count
const BASE_PER_PALLET = 85; // $ per pallet before the zone multiplier
const HANDLING = 40; // flat pickup + dock handling

function TruckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18H9M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </svg>
  );
}

const usd = (n: number) => "$" + Math.round(n).toLocaleString();

export function ShippingEstimator() {
  const [regionIdx, setRegionIdx] = useState(0);
  const [unitsRaw, setUnitsRaw] = useState("150");

  const est = useMemo(() => {
    const units = Math.max(1, Number(unitsRaw) || 0);
    const pallets = Math.max(1, Math.ceil(units / UNITS_PER_PALLET));
    const region = REGIONS[regionIdx];
    const mid = HANDLING + pallets * BASE_PER_PALLET * region.mult;
    return {
      pallets,
      low: mid * 0.9,
      high: mid * 1.15,
      perUnit: mid / units,
      days: region.days,
    };
  }, [regionIdx, unitsRaw]);

  return (
    <section className="pt-4 pb-8 px-4 lg:px-8 max-w-7xl mx-auto">
      <div className="rounded-2xl bg-gradient-to-br from-blue-600/[0.07] to-transparent border border-white/8 p-6 lg:p-8">
        <div className="flex items-center gap-2 mb-1">
          <TruckIcon className="w-4 h-4 text-blue-400" />
          <p className="text-xs font-medium text-blue-400 tracking-widest uppercase">Freight</p>
        </div>
        <h2 className="text-2xl lg:text-3xl font-bold text-white mb-1.5">Estimate your shipping</h2>
        <p className="text-gray-500 text-sm mb-6">
          Ballpark the freight to your dock before you commit. Final rates are confirmed at checkout.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
          {/* Inputs */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1.5">
                Deliver to
              </span>
              <select
                value={regionIdx}
                onChange={(e) => setRegionIdx(Number(e.target.value))}
                className="w-full bg-black/30 text-white text-sm border border-white/10 rounded-xl px-3.5 py-3 cursor-pointer focus:outline-none focus:border-blue-500/40"
              >
                {REGIONS.map((r, i) => (
                  <option key={r.label} value={i} className="bg-[#111] text-white">
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1.5">
                Units to ship
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={unitsRaw}
                onChange={(e) => setUnitsRaw(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="e.g. 150"
                className="w-full bg-black/30 text-white text-sm border border-white/10 rounded-xl px-3.5 py-3 tabular-nums focus:outline-none focus:border-blue-500/40"
              />
              <span className="block text-[11px] text-gray-600 mt-1.5">
                ~{est.pallets} pallet{est.pallets === 1 ? "" : "s"} &middot; {UNITS_PER_PALLET} units/pallet
              </span>
            </label>
          </div>

          {/* Estimate */}
          <div className="rounded-xl bg-black/30 border border-blue-500/20 p-5 flex flex-col justify-center">
            <p className="text-[11px] uppercase tracking-widest text-blue-400/80 mb-1">Estimated freight</p>
            <p className="text-2xl font-bold text-white tabular-nums mb-2">
              {usd(est.low)} <span className="text-gray-500 font-normal">–</span> {usd(est.high)}
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span>{usd(est.perUnit)}/unit</span>
              <span className="text-gray-600">&middot;</span>
              <span>{est.days} business days</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
